import BookOutlined from '@mui/icons-material/BookOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { Autocomplete, Button, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { AccessEventsFilter, Door, MobileOpCode, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import SearchAndFilters, { FilterChip, FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { TwoLineCell } from 'v2/components/TextCell/TextCell';
import useAccessEvents from 'v2/hooks/useAccessEvents/useAccessEvents';
import useAccessEventsDataForFilters from 'v2/hooks/useAccessEvents/useAccessEventsDataForFilters';
import { formatDate, getBrowser12HourTime } from 'v2/utils/dateTime';
import ExportModal from './components/ExportModal';
import PreparingExportModal from './components/PreparingExportModal';
import TableWithData from './components/TableWithData';

type DetailKey = 'NO_TOKEN' | 'DOM_UNKNOWN';

interface FilterState {
  access: MobileOpCode | null;
  details: Record<DetailKey, boolean>;
  dateStart: Dayjs | null;
  dateEnd: Dayjs | null;
  user: string | null;
  device: string | null;
}

const detailDefault: Record<DetailKey, boolean> = {
  NO_TOKEN: false,
  DOM_UNKNOWN: false
};

const getVisitorsAutocompleteOptions = (visitors: Visitor[]) =>
  visitors.map((visitor) => ({
    label: visitor.name,
    email: visitor.email,
    id: visitor.id
  }));

const getDevicesAutocompleteOptions = (doors: Door[]) =>
  doors.map((door) => ({
    label: door.name,
    id: door.id
  }));

const Overview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filterState, setFilterState] = useState<FilterState>({
    access: null,
    details: detailDefault,
    dateStart: null,
    dateEnd: null,
    device: null,
    user: null
  });
  const [page, setPage] = useState(0);
  const now = dayjs();

  const apiFilter = useMemo<AccessEventsFilter | null>(() => {
    const filter: AccessEventsFilter = {};
    if (filterState.access) filter.actions = [filterState.access];
    if (filterState.dateStart || filterState.dateEnd) {
      filter.dateRange = {
        rangeStart: (filterState.dateStart ?? dayjs().subtract(30, 'day')).toISOString(),
        rangeEnd: (filterState.dateEnd ?? dayjs()).toISOString()
      };
    }
    if (filterState.user) filter.visitorIds = [filterState.user];
    if (filterState.device) filter.doorIds = [filterState.device];
    if (filterState.details.NO_TOKEN) filter.reason = 'NO_TOKEN';
    else if (filterState.details.DOM_UNKNOWN) filter.reason = 'DOM_UNKNOWN';

    return Object.keys(filter).length > 0 ? filter : null;
  }, [
    filterState.access,
    filterState.dateStart,
    filterState.dateEnd,
    filterState.user,
    filterState.device,
    filterState.details.NO_TOKEN,
    filterState.details.DOM_UNKNOWN
  ]);

  useEffect(() => {
    setPage(0);
  }, [apiFilter]);

  const { accessEvents, rowCount, loading } = useAccessEvents({
    handleFetchError: (message) => showSnackbar(message, { severity: 'error' }),
    translation,
    page,
    filter: apiFilter
  });

  const { doors, doorsLoading, visitors, visitorsLoading } = useAccessEventsDataForFilters({
    handleFetchError: () => showSnackbar(translation.access_events_error_fetching, { severity: 'error' })
  });

  const activeDetails = Object.entries(filterState.details)
    .filter(([, val]) => val)
    .map(([key]) => key);

  const isAnyFilterEnabled =
    filterState.access !== null ||
    activeDetails.length > 0 ||
    filterState.dateStart !== null ||
    filterState.dateEnd !== null;

  const handleExport = () => {
    if (isAnyFilterEnabled) {
      openModal(<ExportModal filter={apiFilter} />);
    } else {
      openModal(<PreparingExportModal filter={null} />);
    }
  };

  const detailItems = (
    [
      ['NO_TOKEN', translation.no_token],
      ['DOM_UNKNOWN', translation.unknown]
    ] as [DetailKey, string][]
  ).map(([key, label]) => ({
    key,
    label,
    checked: filterState.details[key],
    onChange: () =>
      setFilterState((prev) => ({
        ...prev,
        details: { ...prev.details, [key]: !prev.details[key] }
      }))
  }));

  const visitorOptions = useMemo(() => getVisitorsAutocompleteOptions(visitors), [visitors]);

  const sections: FilterSection[] = [
    {
      id: 'access',
      header: translation.access,
      type: 'radio',
      items: [
        {
          key: 'granted',
          label: translation.granted,
          checked: filterState.access === MobileOpCode.AccessGranted,
          onChange: () =>
            setFilterState((prev) => ({
              ...prev,
              access: prev.access === MobileOpCode.AccessGranted ? null : MobileOpCode.AccessGranted
            }))
        },
        {
          key: 'denied',
          label: translation.denied,
          checked: filterState.access === MobileOpCode.AccessDenied,
          onChange: () =>
            setFilterState((prev) => ({
              ...prev,
              access: prev.access === MobileOpCode.AccessDenied ? null : MobileOpCode.AccessDenied
            }))
        }
      ],
      isClearDisabled: filterState.access === null,
      onClear: () => setFilterState((prev) => ({ ...prev, access: null }))
    },
    {
      id: 'date_and_time_range',
      header: translation.date_and_time_range,
      customContent: (
        <Stack spacing={2} py={2}>
          <DateTimePicker
            label={translation.start_date_time}
            value={filterState.dateStart}
            maxDateTime={now}
            ampm={getBrowser12HourTime()}
            closeOnSelect
            onChange={(val) =>
              setFilterState((prev) => ({
                ...prev,
                dateStart: val,
                dateEnd: val && prev.dateEnd && val.isAfter(prev.dateEnd) ? now : prev.dateEnd
              }))
            }
            slotProps={{ textField: { size: 'small', id: 'dateStart-input' } }}
          />
          <DateTimePicker
            label={translation.end_date_time}
            value={filterState.dateEnd}
            maxDateTime={now}
            ampm={getBrowser12HourTime()}
            closeOnSelect
            onChange={(val) =>
              setFilterState((prev) => ({
                ...prev,
                dateEnd: val,
                dateStart:
                  val && prev.dateStart && val.isBefore(prev.dateStart) ? val.subtract(1, 'hour') : prev.dateStart
              }))
            }
            slotProps={{ textField: { size: 'small', id: 'dateEnd-input' } }}
          />
        </Stack>
      ),
      isClearDisabled: filterState.dateStart === null && filterState.dateEnd === null,
      onClear: () => setFilterState((prev) => ({ ...prev, dateStart: null, dateEnd: null }))
    },
    {
      id: 'autocomplete_filters',
      customContent: (
        <Stack spacing={2} py={2}>
          <Autocomplete
            getOptionKey={(option) => option.id}
            disablePortal
            options={visitorOptions}
            value={visitorOptions.find((visitor) => visitor.id === filterState.user) ?? null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} id="user-select" label={translation.all_users} />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <TwoLineCell title={option.label} content={option.email} />
              </li>
            )}
            onChange={(_, newValue) =>
              setFilterState((prev) => ({
                ...prev,
                user: newValue?.id ?? null
              }))
            }
          />
          <Autocomplete
            disablePortal
            options={getDevicesAutocompleteOptions(doors)}
            value={getDevicesAutocompleteOptions(doors).find((door) => door.id === filterState.device) ?? null}
            renderInput={(params) => <TextField {...params} id="device-select" label={translation.all_devices} />}
            onChange={(_, newValue) => setFilterState((prev) => ({ ...prev, device: newValue?.id || null }))}
          />
          <Autocomplete
            disablePortal
            options={detailItems}
            getOptionLabel={(option) => option.label}
            value={detailItems.find((item) => item.checked) || null}
            renderInput={(params) => <TextField {...params} id="details-select" label={translation.all_details} />}
            onChange={(_, newValue) =>
              setFilterState((prev) => ({
                ...prev,
                details: {
                  NO_TOKEN: newValue?.key === 'NO_TOKEN',
                  DOM_UNKNOWN: newValue?.key === 'DOM_UNKNOWN'
                }
              }))
            }
          />
        </Stack>
      )
    }
  ];

  const chips: FilterChip[] = [
    ...(filterState.access !== null
      ? [
          {
            label: filterState.access === MobileOpCode.AccessGranted ? translation.granted : translation.denied,
            onDelete: () => setFilterState((prev) => ({ ...prev, access: null }))
          }
        ]
      : []),
    ...(filterState.user !== null
      ? [
          {
            label: visitors.find((v) => v.id === filterState.user)?.name || filterState.user,
            onDelete: () => setFilterState((prev) => ({ ...prev, user: null }))
          }
        ]
      : []),
    ...(filterState.device !== null
      ? [
          {
            label: doors.find((d) => d.id === filterState.device)?.name || filterState.device,
            onDelete: () => setFilterState((prev) => ({ ...prev, device: null }))
          }
        ]
      : []),
    ...detailItems
      .filter((item) => item.checked)
      .map((item) => ({
        label: item.label,
        onDelete: () => setFilterState((prev) => ({ ...prev, details: { ...prev.details, [item.key]: false } }))
      })),
    ...(filterState.dateStart !== null || filterState.dateEnd !== null
      ? [
          {
            label:
              filterState.dateStart && filterState.dateEnd
                ? `${formatDate(filterState.dateStart.toDate(), 'dateTimeShort')} – ${formatDate(filterState.dateEnd.toDate(), 'dateTimeShort')}`
                : filterState.dateStart
                  ? `${translation.from_date} ${formatDate(filterState.dateStart.toDate(), 'dateTimeShort')}`
                  : `${translation.until_date} ${formatDate(filterState.dateEnd!.toDate(), 'dateTimeShort')}`,
            onDelete: () => setFilterState((prev) => ({ ...prev, dateStart: null, dateEnd: null }))
          }
        ]
      : [])
  ];

  const breadcrumb = { title: translation.access_events, crumbs: [] };
  const hasData = loading || accessEvents.length > 0 || isAnyFilterEnabled;
  const hasResults = accessEvents.length > 0;

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack>
            <Typography variant="h4">{translation.access_events}</Typography>
            <Typography variant="body2" color="textSecondary">
              {translation.these_are_the_events_of_the_last_30_days}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMobile ? 'column-reverse' : 'row'}
          spacing={2}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'flex-start'}
        >
          <SearchAndFilters
            sections={sections}
            chips={chips}
            onClearAll={() => {
              setFilterState({
                access: null,
                details: detailDefault,
                dateStart: null,
                dateEnd: null,
                device: null,
                user: null
              });
            }}
          />
          <Button id="export-events-button" variant="outlined" startIcon={<DownloadOutlined />} onClick={handleExport}>
            {translation.export}
          </Button>
        </Stack>
        <OverviewContent
          loading={loading}
          hasData={hasData}
          hasResults={hasResults}
          table={
            <TableWithData
              rows={accessEvents}
              loading={loading}
              rowCount={rowCount}
              page={page}
              onPageChange={setPage}
            />
          }
          noResults={
            <EmptyStateOverview
              icon={BookOutlined}
              title={translation.no_events_found}
              content={translation.no_results_description}
            />
          }
          noData={
            <EmptyStateOverview
              icon={BookOutlined}
              title={translation.no_events}
              content={translation.no_events_description}
            />
          }
        />
      </Stack>
    </>
  );
};

export default Overview;
